import * as React from 'react';
import { Form, RHFInput } from 'components/form/RHF';
import { useGetContactFormDefaultValues } from './BecomePartnerForm.utils';
import { useTranslation } from 'next-i18next';
import { contactValidationSchema } from './BecomePartnerForm.schema';
import api from 'api/api';
import { useToast } from 'components/Toast';
import RHFTextArea from 'components/form/RHF/RHFTextArea';
import analytics, { ANALYTIC_EVENTS } from 'utils/firebase/analytics';
import { PartnerContactForm } from '@poinz/api';
import Paragraph from 'components/text/Paragraph';
import { CheckCircle2 } from 'lucide-react';

const BecomePartnerForm: React.FC = () => {
  const { t } = useTranslation();
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const { toast } = useToast();

  const defaultValues = useGetContactFormDefaultValues();

  const handleSubmit = React.useCallback(
    async (data: PartnerContactForm & { description: string }) => {
      try {
        await api.backend.partnerContact.post(data);
        analytics.logEvent(ANALYTIC_EVENTS.SUPPORT_TICKET_CREATED, data);
        setIsSubmitted(true);
      } catch (e: any) {
        toast({
          title: e.message || t('api.error'),
          variant: 'error'
        });
      }
    },
    [t, toast]
  );

  return (
    <div className="w-full bg-white dark:bg-neutral-900 border border-neutral-900 dark:border-white px-6 py-[30px] sm:px-8 sm:py-9 rounded-3xl max-w-full sm:max-w-[482px] flex items-center">
      {isSubmitted ? (
        <div className="flex flex-col items-center justify-center max-w-[80%] m-auto">
          <CheckCircle2 className="text-primary mb-8" size={32} />
          <Paragraph className="text-center font-semibold text-heading-mini mb-1">
            {t('becomePartner.form.successTitle')}
          </Paragraph>
          <Paragraph className="text-center text-base">
            {t('becomePartner.form.successDescription')}
          </Paragraph>
        </div>
      ) : (
        <Form
          enableReinitialize
          initialValues={defaultValues}
          onSubmit={handleSubmit}
          validationSchema={contactValidationSchema(t)}
          buttonLabel="general.form.send"
          className="w-full"
        >
          <RHFInput required name="firstName" label="general.form.firstName" showLabel />
          <RHFInput required name="lastName" label="general.form.lastName" showLabel />
          <RHFInput required name="company" label="general.form.company" showLabel />
          <RHFInput required name="email" label="general.form.email" showLabel />
          <RHFInput
            required
            name="phone"
            autoComplete="tel"
            type="tel"
            label="general.form.phoneNumber"
            showLabel
          />
          <RHFTextArea
            placeholder={t('general.form.message') as string}
            required
            name="message"
            label="general.form.message"
            showLabel
          />
        </Form>
      )}
    </div>
  );
};

export default BecomePartnerForm;
